import getSymbolFromCurrency from 'currency-symbol-map'
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import type { PriceInput, ProductCategory } from '@/api/admin-products-types'
import type { ApplicationRule, DiscountDefinition, Promotion } from '@/api/admin-promotions-types'
import { useCreatePromotion, useUpdatePromotion } from '@/api/api-mutations'
import {
	useGetCustomer,
	useGetCustomerGroup,
	useGetDashboardFeatures,
	useGetProduct,
	useGetProductCategory,
	useGetProductCategoryHierarchy,
	useGetProductGroup,
	useListCustomerGroups,
	useListCustomers,
	useListProductGroups,
	useListProducts,
	useListPromotions,
	useListSalesChannelGroups,
	useListSalesChannels,
	useListTaxRates,
	useListUnitsOfMeasure
} from '@/api/react-query'
import { Badge } from '@/components/base/badge'
import { Button } from '@/components/base/button'
import { Combobox } from '@/components/base/combobox'
import { CurrencyInput } from '@/components/base/currency-input'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { Input } from '@/components/base/input'
import { toast, useCreatedToast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, FormHint, FormLabel, useAppForm } from '@/components/form/form'
import { RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import type { Translations } from '@/i18n/i18n-types'
import { ExclamationCircleSolid } from '@/icons/exclamation-circle-solid'
import { Plus } from '@/icons/plus'
import { Trash } from '@/icons/trash'
import { XMarkMini } from '@/icons/x-mark-mini'
import { PrecisionAmount, fromPrecision, getCurrencyDecimals, toPrecision } from '@/utils/currency'
import { useDebounce } from '@/utils/hooks'

import { sortDaysOfWeek, storedPercentageToPercentPoints } from './-promotion-utils'

const DEFAULT_CURRENCY = 'ISK'

type PromotionType = 'discount' | 'bundle' | 'buyXGetY'
type Effect = 'percentage' | 'fixed' | 'override'

// A single time window shared by one or more days of the week. On submit each
// window is flattened into one per-day entry for the API; on load, per-day
// entries that share the same window are grouped back together.
type ScheduleWindow = {
	days: string[]
	fromTime: string
	toTime: string
}

type SlotKind = 'group' | 'category'

type BundleGroup = {
	kind: SlotKind
	groupUuid: string
	categoryUuid: string
	quantity: number | undefined
}

// Buy-X-get-Y "get" groups carry their OWN discount (the BC mapper supports a distinct discount per
// benefit group), so each row edits its own effect/percentage/amount rather than a single shared value.
type BenefitGroup = BundleGroup & {
	effect: 'percentage' | 'fixed'
	discountPercentage: number | undefined
	discountAmount: PrecisionAmount | null
	discountCurrencyCode: string
}

const emptyBundleGroup = (): BundleGroup => ({
	kind: 'group',
	groupUuid: '',
	categoryUuid: '',
	quantity: 1
})

const emptyBenefitGroup = (): BenefitGroup => ({
	...emptyBundleGroup(),
	effect: 'percentage',
	discountPercentage: undefined,
	discountAmount: null,
	discountCurrencyCode: DEFAULT_CURRENCY
})

const slotFromRule = (entry: {
	productGroupUuid?: string
	productCategoryUuid?: string
	quantity: number
}): BundleGroup => ({
	kind: entry.productCategoryUuid ? 'category' : 'group',
	groupUuid: entry.productGroupUuid ?? '',
	categoryUuid: entry.productCategoryUuid ?? '',
	quantity: entry.quantity
})

const slotTargetUuid = (slot: BundleGroup) => (slot.kind === 'category' ? slot.categoryUuid : slot.groupUuid)

const toRuleEntry = (slot: BundleGroup) =>
	slot.kind === 'category'
		? { productCategoryUuid: slot.categoryUuid, quantity: slot.quantity! }
		: { productGroupUuid: slot.groupUuid, quantity: slot.quantity! }

const isFilledSlot = (slot: BundleGroup) => !!slotTargetUuid(slot) && slot.quantity !== undefined && slot.quantity > 0

// An override price targets either a single sales channel or a whole sales channel group (the
// default for every member channel). Channel overrides win over group defaults at read time.
type PriceTargetType = 'channel' | 'group'

type OverridePriceRow = {
	targetType: PriceTargetType
	targetUuid: string
	enabled: boolean
	amount: PrecisionAmount | null
	taxRateUuid: string
	taxInclusive: boolean
}

// Stable key for an override target so rows for channels and groups never collide.
const overrideTargetKey = (targetType: PriceTargetType, targetUuid: string) => `${targetType}:${targetUuid}`

type FormValues = {
	name: string
	displayTitle: string
	salesChannels: string[]
	salesChannelGroups: string[]
	enabled: boolean
	startDate: number | undefined
	endDate: number | undefined
	schedule: ScheduleWindow[]
	promotionType: PromotionType
	// A promotion's targets are additive — it can carry product groups, categories and individual
	// products at once (the BC catalogue sync routinely creates such promotions), so each kind is
	// edited independently rather than through a single exclusive "applies to" choice.
	applyToAllProducts: boolean
	productGroups: string[]
	products: string[]
	productCategories: string[]
	// Customer eligibility is orthogonal to product targeting: empty = everyone, otherwise the promotion
	// only applies to the listed customers / customer groups (how member-only offers are modelled).
	customerGroups: string[]
	customers: string[]
	bundleGroups: BundleGroup[]
	// Buy-X-get-Y "get" groups; each carries its own discount (see BenefitGroup).
	benefitGroups: BenefitGroup[]
	effect: Effect
	discountPercentage: number | undefined
	discountAmount: PrecisionAmount | null
	discountCurrencyCode: string
	includeFees: boolean
	overridePrices: OverridePriceRow[]
	rank: number | undefined
	// ALL = every selling unit; BASE = variant base unit; otherwise a catalogue unit uuid.
	sellingUnit: string
}

const SELLING_UNIT_ALL = 'ALL'
const SELLING_UNIT_BASE = 'BASE'

const sellingUnitFromPromotion = (promotion?: Promotion) => {
	if (!promotion || promotion.appliesToAllUnits) return SELLING_UNIT_ALL
	return promotion.unitOfMeasure?.uuid ?? SELLING_UNIT_BASE
}

const UPPER_DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'] as const

const formatTime = (t?: { hour: number; minute: number }) =>
	t ? `${String(t.hour).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}` : ''

/**
 * Percentage discounts are stored as a fraction where `fromPrecision(value) = percent / 100`
 * (e.g. 20% -> { value: 2000, precision: 4 } -> 0.2). The order pricing engine multiplies the
 * line total by this fraction, so the UI must encode/decode consistently.
 */
const encodePercentage = (percent: number) => ({ value: Math.round(percent * 100), precision: 4 })
const decodePercentage = (amount: PrecisionAmount) => storedPercentageToPercentPoints(amount)

const buildDefaultValues = (promotion?: Promotion): FormValues => {
	if (!promotion) {
		return {
			name: '',
			displayTitle: '',
			salesChannels: [],
			salesChannelGroups: [],
			enabled: true,
			startDate: undefined,
			endDate: undefined,
			schedule: [],
			promotionType: 'discount',
			applyToAllProducts: true,
			productGroups: [],
			products: [],
			productCategories: [],
			customerGroups: [],
			customers: [],
			bundleGroups: [emptyBundleGroup()],
			benefitGroups: [emptyBenefitGroup()],
			effect: 'percentage',
			discountPercentage: undefined,
			discountAmount: null,
			discountCurrencyCode: DEFAULT_CURRENCY,
			includeFees: false,
			overridePrices: [],
			rank: undefined,
			sellingUnit: SELLING_UNIT_ALL
		}
	}

	const rule = promotion.applicationRule
	const isBundle = rule?.type === 'X_ITEMS_FROM_Y_PRODUCT_GROUPS'
	const isBuyXGetY = rule?.type === 'BUY_X_GET_Y'
	const def = promotion.discountDefinition
	// Buy-X-get-Y carries its discount per benefit entry (loaded into each benefit row below), so the
	// global effect fields are only driven by the top-level discountDefinition (bundle/discount types).
	const effectDef = def

	const effect: Effect = promotion.shouldOverridePrice
		? 'override'
		: effectDef?.type === 'FIXED_AMOUNT'
			? 'fixed'
			: 'percentage'

	const overridePrices: OverridePriceRow[] = (promotion.prices ?? [])
		.filter(price => !price.priceInfo.variablePrice)
		.map(price => ({
			targetType: price.salesChannelGroup ? ('group' as const) : ('channel' as const),
			targetUuid: price.salesChannelGroup?.uuid ?? price.salesChannel?.uuid ?? '',
			enabled: true,
			amount: price.priceInfo.variablePrice ? null : (price.priceInfo.amount as PrecisionAmount),
			taxRateUuid: price.priceInfo.taxRateUuid,
			taxInclusive: price.priceInfo.taxInclusive
		}))

	return {
		name: promotion.name,
		displayTitle: promotion.displayTitle ?? '',
		salesChannels: promotion.salesChannels?.map(c => c.uuid) ?? [],
		salesChannelGroups: promotion.salesChannelGroups?.map(c => c.uuid) ?? [],
		enabled: promotion.enabled,
		startDate: promotion.startDate,
		endDate: promotion.endDate,
		schedule: (promotion.daysOfWeek ?? [])
			.reduce<ScheduleWindow[]>((windows, d) => {
				const fromTime = formatTime(d.activeFromTime)
				const toTime = formatTime(d.activeToTime)
				const existing = windows.find(w => w.fromTime === fromTime && w.toTime === toTime)
				if (existing) {
					existing.days.push(d.dayOfWeek)
				} else {
					windows.push({ days: [d.dayOfWeek], fromTime, toTime })
				}
				return windows
			}, [])
			.map(w => ({ ...w, days: sortDaysOfWeek(w.days) })),
		promotionType: isBuyXGetY ? 'buyXGetY' : isBundle ? 'bundle' : 'discount',
		applyToAllProducts: promotion.applyToAllProducts,
		productGroups: promotion.productGroupUuids ?? [],
		products: promotion.productUuids ?? [],
		productCategories: promotion.productCategoryUuids ?? [],
		customerGroups: promotion.customerGroupUuids ?? [],
		customers: promotion.customerUuids ?? [],
		bundleGroups:
			rule?.type === 'X_ITEMS_FROM_Y_PRODUCT_GROUPS'
				? rule.products.map(slotFromRule)
				: rule?.type === 'BUY_X_GET_Y'
					? rule.qualifying.map(slotFromRule)
					: [emptyBundleGroup()],
		benefitGroups:
			rule?.type === 'BUY_X_GET_Y' && rule.benefit.length > 0
				? rule.benefit.map(p => {
						const d = p.discount
						return {
							...slotFromRule(p),
							quantity: p.quantity,
							effect: d?.type === 'FIXED_AMOUNT' ? ('fixed' as const) : ('percentage' as const),
							discountPercentage:
								d?.type === 'PERCENTAGE'
									? decodePercentage(d.percentage as PrecisionAmount)
									: undefined,
							discountAmount: d?.type === 'FIXED_AMOUNT' ? (d.amount as PrecisionAmount) : null,
							discountCurrencyCode: d?.type === 'FIXED_AMOUNT' ? d.currencyCode : DEFAULT_CURRENCY
						}
					})
				: [emptyBenefitGroup()],
		effect,
		discountPercentage:
			effectDef?.type === 'PERCENTAGE' ? decodePercentage(effectDef.percentage as PrecisionAmount) : undefined,
		discountAmount: effectDef?.type === 'FIXED_AMOUNT' ? (effectDef.amount as PrecisionAmount) : null,
		discountCurrencyCode: effectDef?.type === 'FIXED_AMOUNT' ? effectDef.currencyCode : DEFAULT_CURRENCY,
		includeFees: promotion.includeFees,
		overridePrices,
		rank: promotion.rank,
		sellingUnit: sellingUnitFromPromotion(promotion)
	}
}

export function PromotionForm({ promotion }: { promotion?: Promotion }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const createdToast = useCreatedToast()
	const isEdit = !!promotion

	const createPromotion = useCreatePromotion()
	const updatePromotion = useUpdatePromotion()

	const salesChannels = useListSalesChannels({})
	const salesChannelGroups = useListSalesChannelGroups({})
	const taxRates = useListTaxRates()
	const unitsEnabled = useGetDashboardFeatures().data?.unitsOfMeasureEnabled === true
	const catalogUnits = useListUnitsOfMeasure({ enabled: unitsEnabled })
	const synced = !!promotion?.thirdPartySource
	// The list endpoint caps `limit` at 100; requesting more fails validation and
	// returns no groups. Load the first 100 and let the Combobox filter client-side.
	const productGroups = useListProductGroups({ limit: 100 })
	const categoryHierarchy = useGetProductCategoryHierarchy({})

	// Ranks aren't unique; when editing, surface a non-blocking warning if the chosen
	// priority is already used by another promotion. Only needed while editing (the
	// rank field is hidden on create, where the rank is auto-assigned).
	const existingPromotions = useListPromotions({ limit: '500' }, { enabled: isEdit })
	const otherPromotionsByRank = useMemo(
		() => (existingPromotions.data?.promotions ?? []).filter(p => p.uuid !== promotion?.uuid),
		[existingPromotions.data, promotion?.uuid]
	)

	const [productSearch, setProductSearch] = useState('')
	const debouncedProductSearch = useDebounce(productSearch)
	const products = useListProducts({ q: debouncedProductSearch, limit: 50 })

	// The customer list is paginated and can be large, so search server-side (mirrors the product search).
	const [customerSearch, setCustomerSearch] = useState('')
	const debouncedCustomerSearch = useDebounce(customerSearch)
	const customers = useListCustomers({ q: debouncedCustomerSearch, limit: 50 })
	// Groups are paginated too; search server-side so a group beyond the first page can still be found.
	const [customerGroupSearch, setCustomerGroupSearch] = useState('')
	const debouncedCustomerGroupSearch = useDebounce(customerGroupSearch)
	const customerGroups = useListCustomerGroups({ q: debouncedCustomerGroupSearch, limit: 50 })

	const salesChannelOptions = useMemo(
		() => salesChannels.data?.map(sc => ({ value: sc.uuid, label: sc.name })) ?? [],
		[salesChannels.data]
	)
	const salesChannelGroupOptions = useMemo(
		() => salesChannelGroups.data?.map(g => ({ value: g.uuid, label: g.name })) ?? [],
		[salesChannelGroups.data]
	)
	const currencyOf = useCallback(
		(salesChannelUuid: string) =>
			salesChannels.data?.find(sc => sc.uuid === salesChannelUuid)?.currencyCode ?? DEFAULT_CURRENCY,
		[salesChannels.data]
	)
	// A group has no currency of its own, so use a member channel's currency (they share one in
	// practice). Falls back to the default when the group has no loaded members.
	const currencyOfGroup = useCallback(
		(groupUuid: string) =>
			salesChannels.data?.find(sc => sc.group?.uuid === groupUuid)?.currencyCode ?? DEFAULT_CURRENCY,
		[salesChannels.data]
	)
	// Resolves the currency for an override target regardless of whether it is a channel or group.
	const currencyOfTarget = useCallback(
		(targetType: PriceTargetType, targetUuid: string) =>
			targetType === 'group' ? currencyOfGroup(targetUuid) : currencyOf(targetUuid),
		[currencyOf, currencyOfGroup]
	)
	const currencyOptions = useMemo(() => {
		const codes = new Set<string>([DEFAULT_CURRENCY])
		salesChannels.data?.forEach(sc => codes.add(sc.currencyCode))
		return [...codes].map(code => ({ value: code, label: code }))
	}, [salesChannels.data])

	const groupOptions = useMemo(
		() => productGroups.data?.groups?.map(g => ({ value: g.uuid, label: g.name })) ?? [],
		[productGroups.data]
	)
	const categoryOptions = useMemo(() => {
		const flatten = (nodes: ProductCategory[], level = 0): { value: string; label: string }[] =>
			nodes.flatMap(node => [
				{ value: node.uuid, label: `${level > 0 ? `${'— '.repeat(level)}` : ''}${node.name}` },
				...flatten(node.children ?? [], level + 1)
			])
		return flatten(categoryHierarchy.data?.hierarchy ?? [])
	}, [categoryHierarchy.data])
	const productOptions = useMemo(
		() => products.data?.products?.map(p => ({ value: p.uuid, label: p.title })) ?? [],
		[products.data]
	)
	const customerGroupOptions = useMemo(
		() => customerGroups.data?.customerGroups?.map(g => ({ value: g.uuid, label: g.name })) ?? [],
		[customerGroups.data]
	)
	const customerLabel = useCallback(
		(c: { firstName?: string; lastName?: string; email?: string; phone?: string; uuid: string }) =>
			[c.firstName, c.lastName].filter(Boolean).join(' ').trim() || c.email || c.phone || c.uuid,
		[]
	)
	const customerOptions = useMemo(
		() => customers.data?.customers?.map(c => ({ value: c.uuid, label: customerLabel(c) })) ?? [],
		[customers.data, customerLabel]
	)
	const taxRateOptions = useMemo(
		() => taxRates.data?.map(t => ({ value: t.uuid, label: t.name })) ?? [],
		[taxRates.data]
	)
	const defaultTaxRateUuid = taxRates.data?.[0]?.uuid ?? ''

	const form = useAppForm({
		defaultValues: buildDefaultValues(promotion),
		onSubmit: async ({ value }) => {
			const parseTime = (t: string) => {
				if (!t) return undefined
				const [h, m] = t.split(':').map(Number)
				return { hour: h, minute: m, second: 0 }
			}
			// Flatten each time window into one entry per selected day.
			const dayEntries = value.schedule.flatMap(window =>
				window.days.map(day => ({
					dayOfWeek: day.toUpperCase() as (typeof UPPER_DAYS)[number],
					activeFromTime: parseTime(window.fromTime),
					activeToTime: parseTime(window.toTime)
				}))
			)
			const daysOfWeek = dayEntries.length > 0 ? dayEntries : undefined

			const isBundle = value.promotionType === 'bundle'
			const isBuyXGetY = value.promotionType === 'buyXGetY'

			// The global effect fields don't apply to buy-X-get-Y (its discount is per benefit row).
			if (!isBuyXGetY && value.effect === 'percentage' && value.discountPercentage === undefined) {
				toast.error(L.Promotions.Create.Validation.PercentageRequired())
				return
			}

			const buildDiscountDefinition = (): DiscountDefinition => {
				if (value.effect === 'fixed') {
					return {
						type: 'FIXED_AMOUNT' as const,
						amount: value.discountAmount ?? toPrecision(0, getCurrencyDecimals(value.discountCurrencyCode)),
						currencyCode: value.discountCurrencyCode
					}
				}
				return {
					type: 'PERCENTAGE' as const,
					percentage: encodePercentage(value.discountPercentage ?? 0)
				}
			}

			// Build a benefit group's own discount from its per-row effect fields.
			const buildBenefitDiscount = (g: BenefitGroup): DiscountDefinition =>
				g.effect === 'fixed'
					? {
							type: 'FIXED_AMOUNT' as const,
							amount: g.discountAmount ?? toPrecision(0, getCurrencyDecimals(g.discountCurrencyCode)),
							currencyCode: g.discountCurrencyCode
						}
					: {
							type: 'PERCENTAGE' as const,
							percentage: encodePercentage(g.discountPercentage ?? 0)
						}

			let applicationRule: ApplicationRule | undefined = undefined
			// `undefined` leaves a target kind untouched server-side; an empty array clears it. Only send
			// an array for a kind the current mode actually lets the user edit, so saving a promotion can
			// never silently disconnect targets the form didn't show (bundle modes don't edit products).
			let productGroupUuids: string[] | undefined = undefined
			let productUuids: string[] | undefined = undefined
			let productCategoryUuids: string[] | undefined = undefined
			let applyToAllProducts = false
			let shouldOverridePrice = false
			let prices: PriceInput[] | undefined = undefined
			let discountDefinition: DiscountDefinition | undefined = undefined

			if (isBuyXGetY) {
				const validQualifying = value.bundleGroups.filter(isFilledSlot)
				const validBenefit = value.benefitGroups.filter(isFilledSlot)
				if (validQualifying.length === 0) {
					toast.error(L.Promotions.Create.Validation.BundleGroupsRequired())
					return
				}
				if (validBenefit.length === 0) {
					toast.error(L.Promotions.Create.Validation.BenefitGroupsRequired())
					return
				}
				// A slot cannot be both qualifying and benefit (the API rejects it, and same-group "buy one
				// get one" is unsupported). Catch it here for a clearer message. Ancestor/descendant
				// categories are allowed — the matching gives each cart unit to at most one slot, so
				// "buy 2 from Beverages, get 1 Cola free" prices correctly.
				const slotKey = (g: BundleGroup | BenefitGroup) => `${g.kind}:${slotTargetUuid(g)}`
				const qualifyingKeys = new Set(validQualifying.map(slotKey))
				const hasOverlap = validBenefit.some(g => qualifyingKeys.has(slotKey(g)))
				if (hasOverlap) {
					toast.error(L.Promotions.Create.Validation.QualifyingBenefitOverlap())
					return
				}
				// Each benefit group carries its own discount; validate every percentage row (100% = free).
				for (const g of validBenefit) {
					if (g.effect === 'percentage') {
						if (g.discountPercentage === undefined) {
							toast.error(L.Promotions.Create.Validation.PercentageRequired())
							return
						}
						if (g.discountPercentage < 0 || g.discountPercentage > 100) {
							toast.error(L.Promotions.Create.Validation.PercentageRange())
							return
						}
					}
				}
				applicationRule = {
					type: 'BUY_X_GET_Y' as const,
					qualifying: validQualifying.map(toRuleEntry),
					benefit: validBenefit.map(g => ({
						...toRuleEntry(g),
						discount: buildBenefitDiscount(g)
					}))
				}
				const slots = [...validQualifying, ...validBenefit]
				productGroupUuids = [...new Set(slots.filter(s => s.kind === 'group').map(s => s.groupUuid))]
				productCategoryUuids = [...new Set(slots.filter(s => s.kind === 'category').map(s => s.categoryUuid))]
			} else if (isBundle) {
				const validGroups = value.bundleGroups.filter(isFilledSlot)
				if (validGroups.length === 0) {
					toast.error(L.Promotions.Create.Validation.BundleGroupsRequired())
					return
				}
				applicationRule = {
					type: 'X_ITEMS_FROM_Y_PRODUCT_GROUPS' as const,
					products: validGroups.map(toRuleEntry)
				}
				productGroupUuids = [...new Set(validGroups.filter(s => s.kind === 'group').map(s => s.groupUuid))]
				productCategoryUuids = [
					...new Set(validGroups.filter(s => s.kind === 'category').map(s => s.categoryUuid))
				]

				if (value.effect === 'override') {
					const isSelectedTarget = (r: OverridePriceRow) =>
						r.targetType === 'group'
							? value.salesChannelGroups.includes(r.targetUuid)
							: value.salesChannels.includes(r.targetUuid)
					const rows = value.overridePrices.filter(r => isSelectedTarget(r) && r.enabled && r.amount)
					if (rows.length === 0) {
						toast.error(L.Promotions.Create.Validation.OverridePriceRequired())
						return
					}
					shouldOverridePrice = true
					prices = rows.map(r => ({
						...(r.targetType === 'group'
							? { salesChannelGroupUuid: r.targetUuid }
							: { salesChannelUuid: r.targetUuid }),
						currencyCode: currencyOfTarget(r.targetType, r.targetUuid),
						priceInfo: {
							variablePrice: false as const,
							amount: r.amount!,
							taxInclusive: r.taxInclusive,
							taxRateUuid: r.taxRateUuid || defaultTaxRateUuid
						}
					}))
				} else {
					discountDefinition = buildDiscountDefinition()
				}
			} else {
				// Plain discount: the three target kinds are edited together and are additive, so all
				// three are always sent (an emptied one is meant to be cleared).
				applyToAllProducts = value.applyToAllProducts
				if (applyToAllProducts) {
					productGroupUuids = []
					productUuids = []
					productCategoryUuids = []
				} else {
					if (
						value.productGroups.length === 0 &&
						value.productCategories.length === 0 &&
						value.products.length === 0
					) {
						toast.error(L.Promotions.Create.Validation.ScopeTargetsRequired())
						return
					}
					productGroupUuids = value.productGroups
					productCategoryUuids = value.productCategories
					productUuids = value.products
				}
				discountDefinition = buildDiscountDefinition()
			}

			const body = {
				name: value.name,
				displayTitle: value.displayTitle.trim() || undefined,
				salesChannels: value.salesChannels,
				salesChannelGroups: value.salesChannelGroups,
				startDate: value.startDate,
				endDate: value.endDate,
				daysOfWeek,
				discountDefinition,
				applicationRule,
				shouldOverridePrice,
				prices,
				applyToAllProducts,
				productGroupUuids,
				productUuids,
				productCategoryUuids,
				// Always sent (both create and update): an emptied list clears the targeting so the
				// promotion reverts to applying to everyone.
				customerGroupUuids: value.customerGroups,
				customerUuids: value.customers,
				includeFees: value.includeFees,
				enabled: value.enabled,
				rank: value.rank,
				...(unitsEnabled
					? {
							appliesToAllUnits: value.sellingUnit === SELLING_UNIT_ALL,
							unitOfMeasureUuid:
								value.sellingUnit === SELLING_UNIT_ALL || value.sellingUnit === SELLING_UNIT_BASE
									? undefined
									: value.sellingUnit
						}
					: {})
			}

			const result = isEdit
				? await updatePromotion({ path: { promotionUuid: promotion!.uuid }, body })
				: await createPromotion({ body })

			if (result.isSuccess) {
				if (isEdit) {
					toast.success(L.Promotions.UpdateSuccess())
				} else {
					createdToast(L.Promotions.CreateSuccess(), {
						to: '/promotions/$promotionUuid',
						params: { promotionUuid: result.response.uuid }
					})
				}
				handleSuccess()
			} else {
				// Application rules have server-side checks the form can't fully pre-empt (rule targets
				// must be attached to the promotion, an entry must name exactly one group or category),
				// so show what the server said rather than a generic failure.
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	const allDayOptions = useMemo(
		() => [
			{ value: 'MONDAY', label: L.DaysOfWeek.Monday() },
			{ value: 'TUESDAY', label: L.DaysOfWeek.Tuesday() },
			{ value: 'WEDNESDAY', label: L.DaysOfWeek.Wednesday() },
			{ value: 'THURSDAY', label: L.DaysOfWeek.Thursday() },
			{ value: 'FRIDAY', label: L.DaysOfWeek.Friday() },
			{ value: 'SATURDAY', label: L.DaysOfWeek.Saturday() },
			{ value: 'SUNDAY', label: L.DaysOfWeek.Sunday() }
		],
		[L]
	)

	const addScheduleWindow = useCallback(() => {
		form.pushFieldValue('schedule', { days: [], fromTime: '', toTime: '' })
	}, [form])

	const discountEffectOptions = (isBundle: boolean) => [
		{ value: 'percentage', label: L.Promotions.Create.Percentage() },
		{ value: 'fixed', label: L.Promotions.Create.FixedAmount() },
		...(isBundle ? [{ value: 'override', label: L.Promotions.Create.OverridePrice() }] : [])
	]

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{isEdit ? L.Promotions.EditTitle() : L.Promotions.CreateTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					{/* Basic info */}
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{
								onChange: ({ value }) =>
									value.trim().length === 0
										? L.Promotions.Create.Validation.NameRequired()
										: undefined
							}}
							children={field => (
								<field.TextField
									autoFocus
									label={L.Fields.Name()}
									placeholder={L.Promotions.Create.NamePlaceholder()}
								/>
							)}
						/>
						<div className='flex flex-col gap-y-1.5'>
							<form.AppField
								name='displayTitle'
								children={field => (
									<field.TextField
										label={L.Promotions.Create.DisplayTitle()}
										placeholder={L.Promotions.Create.DisplayTitlePlaceholder()}
										optional
									/>
								)}
							/>
							<FormHint className='block'>{L.Promotions.Create.DisplayTitleHint()}</FormHint>
						</div>
						<form.AppField
							name='salesChannelGroups'
							listeners={{
								// Mirror the channel listener for groups: pre-seed a group-scoped override row.
								onChange: ({ value: selected }) => {
									const overridePrices = form.getFieldValue('overridePrices')
									const missing = selected.filter(
										uuid =>
											!overridePrices.some(r => r.targetType === 'group' && r.targetUuid === uuid)
									)
									if (missing.length === 0) return
									form.setFieldValue('overridePrices', [
										...overridePrices,
										...missing.map(uuid => ({
											targetType: 'group' as const,
											targetUuid: uuid,
											enabled: true,
											amount: null,
											taxRateUuid: defaultTaxRateUuid,
											taxInclusive: true
										}))
									])
								}
							}}
							children={field => (
								<field.ComboboxField
									label={L.Promotions.Create.SalesChannelGroups()}
									placeholder={L.Promotions.Create.SelectSalesChannelGroups()}
									optional
									optionsLoading={salesChannelGroups.isLoading}
									options={salesChannelGroupOptions}
								/>
							)}
						/>
						<form.AppField
							name='salesChannels'
							listeners={{
								// Ensure an override-price row exists for each newly selected channel.
								// Reads the current value fresh (no store subscription) to avoid loops.
								onChange: ({ value: selected }) => {
									const overridePrices = form.getFieldValue('overridePrices')
									const missing = selected.filter(
										uuid =>
											!overridePrices.some(
												r => r.targetType === 'channel' && r.targetUuid === uuid
											)
									)
									if (missing.length === 0) return
									form.setFieldValue('overridePrices', [
										...overridePrices,
										...missing.map(uuid => ({
											targetType: 'channel' as const,
											targetUuid: uuid,
											enabled: true,
											amount: null,
											taxRateUuid: defaultTaxRateUuid,
											taxInclusive: true
										}))
									])
								}
							}}
							children={field => (
								<field.ComboboxField
									label={L.Fields.SalesChannel()}
									placeholder={L.Promotions.Create.SelectSalesChannels()}
									optional
									options={salesChannelOptions}
								/>
							)}
						/>
						<form.AppField
							name='enabled'
							children={field => <field.BooleanField label={L.Fields.Enabled()} />}
						/>
						{/* Rank is auto-assigned on create (max+1); only expose it when editing. */}
						{isEdit && (
							<div className='flex flex-col gap-y-1.5'>
								<form.AppField
									name='rank'
									validators={{
										onChange: ({ value }) =>
											value !== undefined && value < 0
												? L.Promotions.Create.Validation.RankRange()
												: undefined
									}}
									children={field => (
										<field.NumberField
											label={L.Promotions.Create.Rank()}
											min={0}
											step={1}
											optional
										/>
									)}
								/>
								<FormHint className='block'>{L.Promotions.Create.RankHint()}</FormHint>
								<form.Subscribe selector={s => s.values.rank}>
									{rank => {
										const collisions =
											rank === undefined ? [] : otherPromotionsByRank.filter(p => p.rank === rank)
										return collisions.length > 0 ? (
											<div className='flex items-start gap-x-1.5 rounded-md bg-ui-tag-orange-bg px-2 py-1.5 text-ui-tag-orange-text'>
												<ExclamationCircleSolid className='mt-0.5 shrink-0 text-ui-tag-orange-icon' />
												<span className='txt-small'>
													{L.Promotions.Create.RankInUse({
														names: collisions.map(c => c.name).join(', ')
													})}
												</span>
											</div>
										) : null
									}}
								</form.Subscribe>
							</div>
						)}
					</FormFieldGroup>

					{/* Schedule */}
					<FormFieldGroup>
						<div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
							<form.AppField
								name='startDate'
								children={field => <field.DateField label={L.Fields.StartDate()} optional />}
							/>
							<form.AppField
								name='endDate'
								children={field => <field.DateField label={L.Fields.EndDate()} optional />}
							/>
						</div>
					</FormFieldGroup>

					{/* Days of week */}
					<div className='flex flex-col'>
						<FormLabel optional>{L.Promotions.Create.DaysOfWeek()}</FormLabel>
						<form.Field name='schedule' mode='array'>
							{field => (
								<div className='mt-2 flex flex-col gap-y-2'>
									{field.state.value.map((entry, i) => {
										// A day can only belong to one window, so hide days already
										// used by the other windows from this one's picker.
										const usedByOthers = new Set(
											field.state.value.flatMap((w, j) => (j === i ? [] : w.days))
										)
										return (
											<ScheduleWindowRow
												key={i}
												entry={entry}
												allDays={allDayOptions}
												usedByOthers={usedByOthers}
												onChange={updated => {
													const next = [...field.state.value]
													next[i] = updated
													form.setFieldValue('schedule', next)
												}}
												onRemove={() => field.removeValue(i)}
												L={L}
											/>
										)
									})}
									<form.Subscribe
										selector={s => new Set(s.values.schedule.flatMap(w => w.days)).size}
									>
										{usedDayCount =>
											usedDayCount < 7 && (
												<Button
													type='button'
													variant='secondary'
													size='small'
													className='self-start'
													onClick={addScheduleWindow}
												>
													<Plus className='mr-1' />
													{L.Promotions.Create.AddDay()}
												</Button>
											)
										}
									</form.Subscribe>
								</div>
							)}
						</form.Field>
					</div>

					{/* Promotion type */}
					<FormFieldGroup>
						<form.AppField
							name='promotionType'
							listeners={{
								// Override pricing is bundle-only; revert to a valid effect for other types.
								onChange: ({ value }) => {
									if (value !== 'bundle' && form.getFieldValue('effect') === 'override') {
										form.setFieldValue('effect', 'percentage')
									}
								}
							}}
							children={field => (
								<field.SelectField
									label={L.Promotions.Create.PromotionType()}
									options={[
										{ value: 'discount', label: L.Promotions.Create.TypeDiscount() },
										{ value: 'bundle', label: L.Promotions.Create.TypeBundle() },
										{ value: 'buyXGetY', label: L.Promotions.Create.TypeBuyXGetY() }
									]}
								/>
							)}
						/>
					</FormFieldGroup>

					{/* Scope / bundle groups */}
					<form.Subscribe selector={s => s.values.promotionType}>
						{promotionType =>
							promotionType === 'discount' ? (
								<FormFieldGroup>
									<form.AppField
										name='applyToAllProducts'
										children={field => (
											<field.BooleanField label={L.Promotions.Create.ApplyToAll()} />
										)}
									/>
									{/* Groups, categories and products are additive — a promotion can target any
									    combination, so all three are editable together. */}
									<form.Subscribe selector={s => s.values.applyToAllProducts}>
										{applyToAll =>
											applyToAll ? null : (
												<div className='flex flex-col gap-y-4'>
													<form.AppField
														name='productGroups'
														children={field => (
															<field.ComboboxField
																label={L.Promotions.Create.ProductGroups()}
																placeholder={L.Promotions.Create.SelectProductGroups()}
																optionsLoading={productGroups.isLoading}
																options={groupOptions}
															/>
														)}
													/>
													<form.AppField
														name='productCategories'
														children={field => (
															<field.ComboboxField
																label={L.Promotions.Create.Categories()}
																placeholder={L.Promotions.Create.SelectCategories()}
																optionsLoading={categoryHierarchy.isLoading}
																options={categoryOptions}
															/>
														)}
													/>
													<div className='flex flex-col gap-y-2'>
														<form.AppField
															name='products'
															children={field => (
																<field.ComboboxField
																	label={L.Promotions.Create.Products()}
																	placeholder={L.Promotions.Create.SelectProducts()}
																	optionsLoading={products.isLoading}
																	searchValue={productSearch}
																	onSearchValueChange={setProductSearch}
																	options={productOptions}
																/>
															)}
														/>
														<form.Subscribe selector={s => s.values.products}>
															{selected =>
																selected.length > 0 && (
																	<div className='flex flex-wrap gap-1'>
																		{selected.map(uuid => (
																			<ProductChip
																				key={uuid}
																				uuid={uuid}
																				onRemove={() =>
																					form.setFieldValue(
																						'products',
																						form
																							.getFieldValue('products')
																							.filter(u => u !== uuid)
																					)
																				}
																			/>
																		))}
																	</div>
																)
															}
														</form.Subscribe>
													</div>
												</div>
											)
										}
									</form.Subscribe>
								</FormFieldGroup>
							) : (
								<div className='flex flex-col gap-y-4'>
									<div className='flex flex-col'>
										<FormLabel
											tooltip={
												promotionType === 'buyXGetY'
													? L.Promotions.Create.QualifyingGroupsHelp()
													: L.Promotions.Create.BundleGroupsHelp()
											}
										>
											{promotionType === 'buyXGetY'
												? L.Promotions.Create.QualifyingGroups()
												: L.Promotions.Create.BundleGroups()}
										</FormLabel>
										<form.Field name='bundleGroups' mode='array'>
											{field => (
												<div className='mt-2 flex flex-col gap-y-2'>
													{field.state.value.map((entry, i) => (
														<BundleGroupRow
															key={i}
															entry={entry}
															groupOptions={groupOptions}
															categoryOptions={categoryOptions}
															groupsLoading={productGroups.isLoading}
															categoriesLoading={categoryHierarchy.isLoading}
															onChange={updated => {
																const next = [...field.state.value]
																next[i] = updated
																form.setFieldValue('bundleGroups', next)
															}}
															onRemove={() => field.removeValue(i)}
															canRemove={field.state.value.length > 1}
															L={L}
														/>
													))}
													<Button
														type='button'
														variant='secondary'
														size='small'
														className='self-start'
														onClick={() =>
															form.pushFieldValue('bundleGroups', emptyBundleGroup())
														}
													>
														<Plus className='mr-1' />
														{L.Promotions.Create.AddBundleGroup()}
													</Button>
												</div>
											)}
										</form.Field>
									</div>

									{promotionType === 'buyXGetY' && (
										<div className='flex flex-col'>
											<FormLabel tooltip={L.Promotions.Create.BenefitGroupsHelp()}>
												{L.Promotions.Create.BenefitGroups()}
											</FormLabel>
											<form.Field name='benefitGroups' mode='array'>
												{field => (
													<div className='mt-2 flex flex-col gap-y-2'>
														{field.state.value.map((entry, i) => (
															<BenefitGroupRow
																key={i}
																entry={entry}
																groupOptions={groupOptions}
																categoryOptions={categoryOptions}
																groupsLoading={productGroups.isLoading}
																categoriesLoading={categoryHierarchy.isLoading}
																currencyOptions={currencyOptions}
																onChange={updated => {
																	const next = [...field.state.value]
																	next[i] = updated
																	form.setFieldValue('benefitGroups', next)
																}}
																onRemove={() => field.removeValue(i)}
																canRemove={field.state.value.length > 1}
																L={L}
															/>
														))}
														<Button
															type='button'
															variant='secondary'
															size='small'
															className='self-start'
															onClick={() =>
																form.pushFieldValue(
																	'benefitGroups',
																	emptyBenefitGroup()
																)
															}
														>
															<Plus className='mr-1' />
															{L.Promotions.Create.AddBundleGroup()}
														</Button>
													</div>
												)}
											</form.Field>
										</div>
									)}
								</div>
							)
						}
					</form.Subscribe>

					{unitsEnabled ? (
						<FormFieldGroup>
							<form.AppField
								name='sellingUnit'
								children={field => (
									<field.SelectField
										label={L.Promotions.Create.SellingUnit()}
										tooltip={L.Promotions.Create.SellingUnitHelp()}
										disabled={synced}
										optionsLoading={catalogUnits.isLoading}
										options={[
											{ value: SELLING_UNIT_ALL, label: L.Promotions.Create.AllUnits() },
											{ value: SELLING_UNIT_BASE, label: L.Promotions.Create.BaseUnit() },
											...(catalogUnits.data ?? []).map(unit => ({
												value: unit.uuid,
												label: unit.name ? `${unit.code} (${unit.name})` : unit.code
											}))
										]}
									/>
								)}
							/>
						</FormFieldGroup>
					) : null}

					{/* Effect — hidden for buy-X-get-Y, whose discount is edited per benefit group above. */}
					<form.Subscribe selector={s => s.values.promotionType === 'buyXGetY'}>
						{isBuyXGetYType =>
							isBuyXGetYType ? null : (
								<FormFieldGroup>
									<form.Subscribe selector={s => s.values.promotionType}>
										{promotionType => (
											<form.AppField
												name='effect'
												children={field => (
													<field.SelectField
														label={L.Promotions.Create.Discount()}
														options={discountEffectOptions(promotionType === 'bundle')}
													/>
												)}
											/>
										)}
									</form.Subscribe>
									<form.Subscribe selector={s => s.values.effect}>
										{effect =>
											effect === 'percentage' ? (
												<form.AppField
													name='discountPercentage'
													validators={{
														onChange: ({ value }) => {
															if (value === undefined) {
																return L.Promotions.Create.Validation.PercentageRequired()
															}
															// Buy-X-get-Y benefit lines allow 100% (a free "get" item); plain
															// percentage discounts must stay below 100%.
															const allowFull =
																form.getFieldValue('promotionType') === 'buyXGetY'
															const invalid =
																value < 0 || value > 100 || (!allowFull && value >= 100)
															return invalid
																? L.Promotions.Create.Validation.PercentageRange()
																: undefined
														}
													}}
													children={field => (
														<field.NumberField
															label={L.Promotions.Create.DiscountPercentage()}
															min={0}
															max={100}
															placeholder='20'
														/>
													)}
												/>
											) : effect === 'fixed' ? (
												<div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
													<form.Subscribe selector={s => s.values.discountCurrencyCode}>
														{currencyCode => (
															<form.AppField
																name='discountAmount'
																children={field => (
																	<field.CurrencyField
																		label={L.Promotions.Create.DiscountAmount()}
																		currencyCode={currencyCode}
																	/>
																)}
															/>
														)}
													</form.Subscribe>
													<form.AppField
														name='discountCurrencyCode'
														children={field => (
															<field.SelectField
																label={L.Fields.CurrencyCode()}
																options={currencyOptions}
															/>
														)}
													/>
												</div>
											) : (
												<form.Subscribe
													selector={s => ({
														channels: s.values.salesChannels,
														groups: s.values.salesChannelGroups,
														rows: s.values.overridePrices
													})}
												>
													{({ channels, groups, rows }) => (
														<OverridePricesEditor
															targets={[
																...groups.map(uuid => ({
																	targetType: 'group' as const,
																	targetUuid: uuid,
																	label: `${
																		salesChannelGroupOptions.find(
																			o => o.value === uuid
																		)?.label ?? uuid
																	} (${L.Product.Pricing.Group()})`
																})),
																...channels.map(uuid => ({
																	targetType: 'channel' as const,
																	targetUuid: uuid,
																	label:
																		salesChannelOptions.find(o => o.value === uuid)
																			?.label ?? uuid
																}))
															]}
															rows={rows}
															setOverridePrices={next =>
																form.setFieldValue('overridePrices', next)
															}
															currencyOfTarget={currencyOfTarget}
															taxRateOptions={taxRateOptions}
															defaultTaxRateUuid={defaultTaxRateUuid}
															L={L}
														/>
													)}
												</form.Subscribe>
											)
										}
									</form.Subscribe>
									<form.Subscribe
										selector={s => ({ type: s.values.promotionType, effect: s.values.effect })}
									>
										{({ type, effect }) =>
											type === 'bundle' && effect === 'override' ? (
												<form.AppField
													name='includeFees'
													children={field => (
														<field.BooleanField
															label={L.Promotions.Detail.IncludeFees()}
															hint={L.Promotions.Create.IncludeFeesHelp()}
														/>
													)}
												/>
											) : null
										}
									</form.Subscribe>
								</FormFieldGroup>
							)
						}
					</form.Subscribe>

					{/* Customer eligibility — leave empty to apply to everyone, or restrict to specific
					    customers / customer groups (how member-only offers are modelled). */}
					<FormFieldGroup>
						<div className='flex flex-col'>
							<FormLabel optional>{L.Promotions.Create.CustomerEligibility()}</FormLabel>
							<FormHint className='mt-1 block'>{L.Promotions.Create.CustomerEligibilityHint()}</FormHint>
						</div>
						<div className='flex flex-col gap-y-2'>
							<form.AppField
								name='customerGroups'
								children={field => (
									<field.ComboboxField
										label={L.Promotions.Create.CustomerGroups()}
										placeholder={L.Promotions.Create.SelectCustomerGroups()}
										optionsLoading={customerGroups.isLoading}
										searchValue={customerGroupSearch}
										onSearchValueChange={setCustomerGroupSearch}
										options={customerGroupOptions}
										optional
									/>
								)}
							/>
							{/* The option list is capped at 100; chips hydrate each selection individually so a group
							    outside that page still renders and can be removed (mirrors SlotTargetFields). */}
							<form.Subscribe selector={s => s.values.customerGroups}>
								{selected =>
									selected.length > 0 && (
										<div className='flex flex-wrap gap-1'>
											{selected.map(uuid => (
												<CustomerGroupChip
													key={uuid}
													uuid={uuid}
													onRemove={() =>
														form.setFieldValue(
															'customerGroups',
															form.getFieldValue('customerGroups').filter(u => u !== uuid)
														)
													}
												/>
											))}
										</div>
									)
								}
							</form.Subscribe>
						</div>
						<div className='flex flex-col gap-y-2'>
							<form.AppField
								name='customers'
								children={field => (
									<field.ComboboxField
										label={L.Promotions.Create.Customers()}
										placeholder={L.Promotions.Create.SelectCustomers()}
										optionsLoading={customers.isLoading}
										searchValue={customerSearch}
										onSearchValueChange={setCustomerSearch}
										options={customerOptions}
										optional
									/>
								)}
							/>
							<form.Subscribe selector={s => s.values.customers}>
								{selected =>
									selected.length > 0 && (
										<div className='flex flex-wrap gap-1'>
											{selected.map(uuid => (
												<CustomerChip
													key={uuid}
													uuid={uuid}
													onRemove={() =>
														form.setFieldValue(
															'customers',
															form.getFieldValue('customers').filter(u => u !== uuid)
														)
													}
												/>
											))}
										</div>
									)
								}
							</form.Subscribe>
						</div>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					{isEdit ? <form.SaveButton /> : <form.CreateButton />}
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}

function ProductChip({ uuid, onRemove }: { uuid: string; onRemove: () => void }) {
	const product = useGetProduct({ productUuid: uuid })
	return (
		<Badge className='flex items-center gap-x-1'>
			{product.data?.title ?? uuid}
			<button type='button' onClick={onRemove} className='text-ui-fg-muted hover:text-ui-fg-base'>
				<XMarkMini />
			</button>
		</Badge>
	)
}

function CustomerChip({ uuid, onRemove }: { uuid: string; onRemove: () => void }) {
	const { L } = useI18n()
	const customer = useGetCustomer({ customerUuid: uuid })
	const c = customer.data
	const label = c ? [c.firstName, c.lastName].filter(Boolean).join(' ').trim() || c.email || c.phone || uuid : uuid
	return (
		<Badge className='flex items-center gap-x-1'>
			{label}
			<button
				type='button'
				onClick={onRemove}
				aria-label={`${L.General.Delete()} ${label}`}
				className='text-ui-fg-muted hover:text-ui-fg-base'
			>
				<XMarkMini />
			</button>
		</Badge>
	)
}

function CustomerGroupChip({ uuid, onRemove }: { uuid: string; onRemove: () => void }) {
	const { L } = useI18n()
	const group = useGetCustomerGroup({ customerGroupUuid: uuid })
	const label = group.data?.name ?? uuid
	return (
		<Badge className='flex items-center gap-x-1'>
			{label}
			<button
				type='button'
				onClick={onRemove}
				aria-label={`${L.General.Delete()} ${label}`}
				className='text-ui-fg-muted hover:text-ui-fg-base'
			>
				<XMarkMini />
			</button>
		</Badge>
	)
}

function SlotTargetFields({
	entry,
	groupOptions,
	categoryOptions,
	groupsLoading,
	categoriesLoading,
	onChange,
	L
}: {
	entry: BundleGroup
	groupOptions: { value: string; label: string }[]
	categoryOptions: { value: string; label: string }[]
	groupsLoading: boolean
	categoriesLoading: boolean
	onChange: (patch: Partial<BundleGroup>) => void
	L: Translations
}) {
	// The group list is capped at 100; a merchant may have more. If the selected
	// group isn't on that page, fetch it individually so its name still renders
	// (and remains selectable) instead of showing an empty dropdown.
	const missingGroup =
		entry.kind === 'group' && !!entry.groupUuid && !groupOptions.some(o => o.value === entry.groupUuid)
	const missingCategory =
		entry.kind === 'category' && !!entry.categoryUuid && !categoryOptions.some(o => o.value === entry.categoryUuid)
	const selectedGroup = useGetProductGroup({ groupUuid: entry.groupUuid }, { enabled: missingGroup })
	const selectedCategory = useGetProductCategory({ categoryUuid: entry.categoryUuid }, { enabled: missingCategory })
	const resolvedGroupOptions = useMemo(() => {
		if (!missingGroup) return groupOptions
		return [{ value: entry.groupUuid, label: selectedGroup.data?.name ?? entry.groupUuid }, ...groupOptions]
	}, [groupOptions, missingGroup, entry.groupUuid, selectedGroup.data])
	const resolvedCategoryOptions = useMemo(() => {
		if (!missingCategory) return categoryOptions
		return [
			{ value: entry.categoryUuid, label: selectedCategory.data?.name ?? entry.categoryUuid },
			...categoryOptions
		]
	}, [categoryOptions, missingCategory, entry.categoryUuid, selectedCategory.data])

	return (
		<>
			<div className='flex flex-col gap-y-1'>
				<label className='txt-compact-small-plus text-ui-fg-subtle'>{L.Promotions.Create.SlotKind()}</label>
				<Combobox
					options={[
						{ value: 'group', label: L.Promotions.Create.SlotKindGroup() },
						{ value: 'category', label: L.Promotions.Create.SlotKindCategory() }
					]}
					value={entry.kind}
					onChange={value =>
						onChange({
							kind: ((value as SlotKind) ?? 'group') as SlotKind,
							groupUuid: '',
							categoryUuid: ''
						})
					}
				/>
			</div>
			{entry.kind === 'category' ? (
				<div className='flex flex-col gap-y-1'>
					<label className='txt-compact-small-plus text-ui-fg-subtle'>
						{L.Promotions.Create.SlotKindCategory()}
					</label>
					<Combobox
						options={resolvedCategoryOptions}
						optionsLoading={categoriesLoading}
						value={entry.categoryUuid}
						onChange={value => onChange({ categoryUuid: (value as string) ?? '' })}
						placeholder={L.Promotions.Create.SelectCategories()}
					/>
				</div>
			) : (
				<div className='flex flex-col gap-y-1'>
					<label className='txt-compact-small-plus text-ui-fg-subtle'>
						{L.Promotions.Create.ProductGroup()}
					</label>
					<Combobox
						options={resolvedGroupOptions}
						optionsLoading={groupsLoading}
						value={entry.groupUuid}
						onChange={value => onChange({ groupUuid: (value as string) ?? '' })}
						placeholder={L.Promotions.Create.SelectProductGroups()}
					/>
				</div>
			)}
		</>
	)
}

function BundleGroupRow({
	entry,
	groupOptions,
	categoryOptions,
	groupsLoading,
	categoriesLoading,
	onChange,
	onRemove,
	canRemove,
	L
}: {
	entry: BundleGroup
	groupOptions: { value: string; label: string }[]
	categoryOptions: { value: string; label: string }[]
	groupsLoading: boolean
	categoriesLoading: boolean
	onChange: (entry: BundleGroup) => void
	onRemove: () => void
	canRemove: boolean
	L: Translations
}) {
	return (
		<div className='grid grid-cols-1 items-end gap-2 rounded-md border border-ui-border-base p-3 sm:grid-cols-[140px_1fr_120px_32px]'>
			<SlotTargetFields
				entry={entry}
				groupOptions={groupOptions}
				categoryOptions={categoryOptions}
				groupsLoading={groupsLoading}
				categoriesLoading={categoriesLoading}
				onChange={patch => onChange({ ...entry, ...patch })}
				L={L}
			/>
			<div className='flex flex-col gap-y-1'>
				<label className='txt-compact-small-plus text-ui-fg-subtle'>{L.Promotions.Create.ItemCount()}</label>
				<Input
					type='number'
					min={1}
					value={entry.quantity ?? ''}
					onChange={e =>
						onChange({ ...entry, quantity: e.target.value === '' ? undefined : Number(e.target.value) })
					}
				/>
			</div>
			<div className='flex items-end justify-end pb-0.5'>
				{canRemove && (
					<IconButton
						type='button'
						variant='transparent'
						size='small'
						onClick={onRemove}
						tooltipText={L.General.Delete()}
					>
						<Trash />
					</IconButton>
				)}
			</div>
		</div>
	)
}

// Buy-X-get-Y benefit row: group + quantity plus this group's OWN discount (percentage or fixed amount).
function BenefitGroupRow({
	entry,
	groupOptions,
	categoryOptions,
	groupsLoading,
	categoriesLoading,
	currencyOptions,
	onChange,
	onRemove,
	canRemove,
	L
}: {
	entry: BenefitGroup
	groupOptions: { value: string; label: string }[]
	categoryOptions: { value: string; label: string }[]
	groupsLoading: boolean
	categoriesLoading: boolean
	currencyOptions: { value: string; label: string }[]
	onChange: (entry: BenefitGroup) => void
	onRemove: () => void
	canRemove: boolean
	L: Translations
}) {
	return (
		<div className='flex flex-col gap-3 rounded-md border border-ui-border-base p-3'>
			<div className='grid grid-cols-1 items-end gap-2 sm:grid-cols-[140px_1fr_120px_32px]'>
				<SlotTargetFields
					entry={entry}
					groupOptions={groupOptions}
					categoryOptions={categoryOptions}
					groupsLoading={groupsLoading}
					categoriesLoading={categoriesLoading}
					onChange={patch => onChange({ ...entry, ...patch })}
					L={L}
				/>
				<div className='flex flex-col gap-y-1'>
					<label className='txt-compact-small-plus text-ui-fg-subtle'>
						{L.Promotions.Create.ItemCount()}
					</label>
					<Input
						type='number'
						min={1}
						value={entry.quantity ?? ''}
						onChange={e =>
							onChange({ ...entry, quantity: e.target.value === '' ? undefined : Number(e.target.value) })
						}
					/>
				</div>
				<div className='flex items-end justify-end pb-0.5'>
					{canRemove && (
						<IconButton
							type='button'
							variant='transparent'
							size='small'
							onClick={onRemove}
							tooltipText={L.General.Delete()}
						>
							<Trash />
						</IconButton>
					)}
				</div>
			</div>
			<div className='grid grid-cols-1 items-end gap-2 sm:grid-cols-2'>
				<div className='flex flex-col gap-y-1'>
					<label className='txt-compact-small-plus text-ui-fg-subtle'>{L.Promotions.Create.Discount()}</label>
					<Combobox
						options={[
							{ value: 'percentage', label: L.Promotions.Create.Percentage() },
							{ value: 'fixed', label: L.Promotions.Create.FixedAmount() }
						]}
						value={entry.effect}
						onChange={value =>
							onChange({ ...entry, effect: (value as BenefitGroup['effect']) ?? 'percentage' })
						}
					/>
				</div>
				{entry.effect === 'percentage' ? (
					<div className='flex flex-col gap-y-1'>
						<label className='txt-compact-small-plus text-ui-fg-subtle'>
							{L.Promotions.Create.DiscountPercentage()}
						</label>
						<Input
							type='number'
							min={0}
							max={100}
							placeholder='100'
							value={entry.discountPercentage ?? ''}
							onChange={e =>
								onChange({
									...entry,
									discountPercentage: e.target.value === '' ? undefined : Number(e.target.value)
								})
							}
						/>
					</div>
				) : (
					<div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
						<div className='flex flex-col gap-y-1'>
							<label className='txt-compact-small-plus text-ui-fg-subtle'>
								{L.Promotions.Create.DiscountAmount()}
							</label>
							<CurrencyAmountInput
								value={entry.discountAmount}
								currencyCode={entry.discountCurrencyCode}
								onChange={amount => onChange({ ...entry, discountAmount: amount })}
							/>
						</div>
						<div className='flex flex-col gap-y-1'>
							<label className='txt-compact-small-plus text-ui-fg-subtle'>
								{L.Fields.CurrencyCode()}
							</label>
							<Combobox
								options={currencyOptions}
								value={entry.discountCurrencyCode}
								onChange={value =>
									onChange({ ...entry, discountCurrencyCode: (value as string) ?? DEFAULT_CURRENCY })
								}
							/>
						</div>
					</div>
				)}
			</div>
		</div>
	)
}

type OverrideTarget = { targetType: PriceTargetType; targetUuid: string; label: string }

function OverridePricesEditor({
	targets,
	rows,
	setOverridePrices,
	currencyOfTarget,
	taxRateOptions,
	defaultTaxRateUuid,
	L
}: {
	targets: OverrideTarget[]
	rows: OverridePriceRow[]
	setOverridePrices: (rows: OverridePriceRow[]) => void
	currencyOfTarget: (targetType: PriceTargetType, targetUuid: string) => string
	taxRateOptions: { value: string; label: string }[]
	defaultTaxRateUuid: string
	L: Translations
}) {
	if (targets.length === 0) {
		return <FormHintText>{L.Promotions.Create.OverridePriceNoChannels()}</FormHintText>
	}

	const matches = (r: OverridePriceRow, target: OverrideTarget) =>
		r.targetType === target.targetType && r.targetUuid === target.targetUuid

	const updateRow = (target: OverrideTarget, patch: Partial<OverridePriceRow>) => {
		const existing = rows.find(r => matches(r, target))
		const base: OverridePriceRow = existing ?? {
			targetType: target.targetType,
			targetUuid: target.targetUuid,
			enabled: true,
			amount: null,
			taxRateUuid: defaultTaxRateUuid,
			taxInclusive: true
		}
		const nextRow = { ...base, ...patch }
		const next = existing ? rows.map(r => (matches(r, target) ? nextRow : r)) : [...rows, nextRow]
		setOverridePrices(next)
	}

	return (
		<div className='flex flex-col gap-y-2'>
			<FormLabel tooltip={L.Promotions.Create.OverridePriceHelp()}>
				{L.Promotions.Create.OverridePrice()}
			</FormLabel>
			{targets.map(target => {
				const row = rows.find(r => matches(r, target))
				const currencyCode = currencyOfTarget(target.targetType, target.targetUuid)
				return (
					<div
						key={overrideTargetKey(target.targetType, target.targetUuid)}
						className='grid grid-cols-1 items-end gap-2 rounded-md border border-ui-border-base p-3 sm:grid-cols-[1fr_1fr_auto]'
					>
						<div className='flex flex-col gap-y-1'>
							<label className='txt-compact-small-plus text-ui-fg-subtle'>{target.label}</label>
							<CurrencyAmountInput
								value={row?.amount ?? null}
								currencyCode={currencyCode}
								onChange={amount => updateRow(target, { amount, enabled: !!amount })}
							/>
						</div>
						<div className='flex flex-col gap-y-1'>
							<label className='txt-compact-small-plus text-ui-fg-subtle'>{L.Fields.TaxRate()}</label>
							<Combobox
								options={taxRateOptions}
								value={row?.taxRateUuid || defaultTaxRateUuid}
								onChange={value => updateRow(target, { taxRateUuid: (value as string) ?? '' })}
							/>
						</div>
						<label className='txt-compact-small flex items-center gap-x-2 pb-1.5 text-ui-fg-subtle'>
							<input
								type='checkbox'
								aria-label={L.Fields.TaxInclusive()}
								checked={row?.taxInclusive ?? true}
								onChange={e => updateRow(target, { taxInclusive: e.target.checked })}
							/>
							{L.Fields.TaxInclusive()}
						</label>
					</div>
				)
			})}
		</div>
	)
}

function CurrencyAmountInput({
	value,
	onChange,
	currencyCode
}: {
	value: PrecisionAmount | null
	onChange: (value: PrecisionAmount | null) => void
	currencyCode: string
}) {
	const decimals = useMemo(() => {
		try {
			return getCurrencyDecimals(currencyCode)
		} catch {
			return 2
		}
	}, [currencyCode])

	const [inputValue, setInputValue] = useState(value ? fromPrecision(value).toString() : '')

	useEffect(() => {
		const external = value ? fromPrecision(value) : null
		const current = inputValue === '' ? null : parseFloat(inputValue)
		if (external !== current) {
			setInputValue(external === null ? '' : external.toString())
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value])

	return (
		<CurrencyInput
			min={0}
			code={currencyCode}
			symbol={getSymbolFromCurrency(currencyCode) ?? currencyCode}
			value={inputValue}
			onValueChange={(val, _name, values) => {
				val = val ?? ''
				setInputValue(val)
				if (values?.float === null) {
					onChange(null)
					return
				}
				const floatValue = values?.float ?? 0
				if (!isNaN(floatValue)) {
					onChange(toPrecision(floatValue, decimals))
				}
			}}
		/>
	)
}

function FormHintText({ children }: { children: ReactNode }) {
	return <p className='txt-compact-small text-ui-fg-muted'>{children}</p>
}

function ScheduleWindowRow({
	entry,
	allDays,
	usedByOthers,
	onChange,
	onRemove,
	L
}: {
	entry: ScheduleWindow
	allDays: { value: string; label: string }[]
	usedByOthers: Set<string>
	onChange: (entry: ScheduleWindow) => void
	onRemove: () => void
	L: Translations
}) {
	const dayOptions = allDays.filter(d => !usedByOthers.has(d.value))
	const labelOf = (value: string) => allDays.find(d => d.value === value)?.label ?? value

	return (
		<div className='flex flex-col gap-3 rounded-md border border-ui-border-base p-3'>
			<div className='grid grid-cols-1 items-end gap-2 sm:grid-cols-[1fr_1fr_1fr_32px]'>
				<div className='flex flex-col gap-y-1'>
					<label className='txt-compact-small-plus text-ui-fg-subtle'>
						{L.Promotions.Create.DaysOfWeek()}
					</label>
					<Combobox
						options={dayOptions}
						value={entry.days}
						onChange={value => onChange({ ...entry, days: sortDaysOfWeek((value as string[]) ?? []) })}
						placeholder={L.Promotions.Create.SelectDays()}
					/>
				</div>
				<div className='flex flex-col gap-y-1'>
					<label className='txt-compact-small-plus text-ui-fg-subtle'>{L.Fields.FromTime()}</label>
					<Input
						type='time'
						size='base'
						value={entry.fromTime}
						onChange={e => onChange({ ...entry, fromTime: e.target.value })}
					/>
				</div>
				<div className='flex flex-col gap-y-1'>
					<label className='txt-compact-small-plus text-ui-fg-subtle'>{L.Fields.ToTime()}</label>
					<Input
						type='time'
						size='base'
						value={entry.toTime}
						onChange={e => onChange({ ...entry, toTime: e.target.value })}
					/>
				</div>
				<div className='flex items-end justify-end pb-0.5'>
					<IconButton
						type='button'
						variant='transparent'
						size='small'
						onClick={onRemove}
						tooltipText={L.General.Delete()}
					>
						<Trash />
					</IconButton>
				</div>
			</div>
			{entry.days.length > 0 && (
				<div className='flex flex-wrap gap-1'>
					{sortDaysOfWeek(entry.days).map(day => (
						<Badge key={day} className='flex items-center gap-x-1'>
							{labelOf(day)}
							<button
								type='button'
								onClick={() => onChange({ ...entry, days: entry.days.filter(d => d !== day) })}
								className='text-ui-fg-muted hover:text-ui-fg-base'
							>
								<XMarkMini />
							</button>
						</Badge>
					))}
				</div>
			)}
		</div>
	)
}
